import React from "react";
import {DataGrid, GridColDef, GridActionsCellItem, GridToolbarContainer, GridRenderCellParams} from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import {Button, Chip, Tooltip, Typography} from "@mui/material";
import {Box} from "@mui/joy";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import IServiceOrder from "../../../types/service-order.type";
import AddIcon from "@mui/icons-material/Add";
import WorkScopeModal from "./WorkScopeModal";
import IWorkScope from "../../../types/work-scope.type";
import ITechnique from "../../../types/technique.type";
import IUser from "../../../types/user.type";
import ConfirmationModal from "../../utils/ConfirmationModal";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import {v4 as uuidv4} from "uuid";
import VisibilityIcon from "@mui/icons-material/Visibility";

interface Props {
  serviceOrder: IServiceOrder;
  setServiceOrder: (updatedServiceOrder: IServiceOrder | ((prev: IServiceOrder) => IServiceOrder)) => void;
  techniques: ITechnique[],
  users: IUser[],
  readOnly?: boolean;
}

const WorkScopesList: React.FC<Props> = ({serviceOrder, setServiceOrder, techniques, users, readOnly = false}) => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = React.useState(false);
  const [selectedWorkScope, setSelectedWorkScope] = React.useState<IWorkScope>();

  const handleConfirmationModalClose = () => {
    setSelectedWorkScope(undefined);
    setConfirmationModalOpen(false);
  }

  const handleDeleteClick = (workScope: IWorkScope) => () => {
    setSelectedWorkScope(workScope);
    setConfirmationModalOpen(true);
  };

  const onConfirm = () => {
    setServiceOrder(prev => ({
      ...prev,
      workScopes: prev.workScopes.filter(ws => ws.id !== selectedWorkScope?.id),
    }));

    setSelectedWorkScope(undefined);
    setConfirmationModalOpen(false);
  };

  const handleModalClose = () => {
    setSelectedWorkScope(undefined);
    setModalOpen(false);
  }

  const handleAddClick = () => {
    setSelectedWorkScope(undefined);
    setModalOpen(true);
  };

  const handleEditClick = (workScope: IWorkScope) => () => {
    setSelectedWorkScope(workScope);
    setModalOpen(true);
  };

  const onSubmit = (workScope: IWorkScope) => {
    const id =
      workScope.id
      ?? globalThis.crypto?.randomUUID?.()
      ?? uuidv4();

    const newWorkScope = {
      ...workScope,
      id: id,
    };

    setServiceOrder(prev => ({
      ...prev,
      workScopes: prev.workScopes.some(ws => ws.id === newWorkScope.id)
        ? prev.workScopes.map(ws => ws.id === newWorkScope.id ? newWorkScope : ws)
        : [...prev.workScopes, newWorkScope],
    }));

    setSelectedWorkScope(undefined);
    setModalOpen(false);
  };

  const columns: GridColDef<IWorkScope>[] = [
    {
      field: 'equipment',
      headerName: 'Equipo',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1
    },
    {
      field: 'technique',
      headerName: 'Tecnica',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const techniquesCount = params.row.techniques?.length || 0;

        if (techniquesCount === 1) {
          return (params.row.techniques[0].name)
        }

        return (
          <Tooltip
            title={
              <Typography>
                {params.row.techniques.map((technique: { name: string }, index: number) => (
                  <div key={index}>- {technique.name}</div>
                ))}
              </Typography>
            }
            arrow
            placement='right'
          >
            <Chip label={`${techniquesCount}`} size="small" sx={{marginTop: -0.2}}/>
          </Tooltip>
        );
      }
    },
    {
      field: 'days',
      headerName: 'Jornadas',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1
    },
    {
      field: 'resources',
      headerName: 'Recursos',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Tooltip
            title={
              <Typography>
                {params.row.resources.map((user: { lastName: string; name: string }, index: number) => (
                  <div key={index}>- {user.lastName}, {user.name}</div>
                ))}
              </Typography>
            }
            arrow
            placement='right'
          >
            <Chip label={`${params.row.resources?.length || 0}`} size="small" sx={{ marginTop: -0.2 }}/>
          </Tooltip>)
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      headerAlign: 'center',
      align: 'center',
      minWidth: 20,
      flex: 1,
      cellClassName: 'actions',
      getActions: (params) => {
        if (readOnly){
          return [
            <GridActionsCellItem
              icon={<VisibilityIcon/>}
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(params.row)}
              color="inherit"
            />
          ];
        } else {
          return [
            <GridActionsCellItem
              icon={<EditIcon/>}
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(params.row)}
              color="inherit"
            />,
            <GridActionsCellItem
              icon={<DeleteForeverIcon/>}
              label="Delete"
              className="textPrimary"
              onClick={handleDeleteClick(params.row)}
              color="inherit"
            />
          ];
        }
      },
    },
  ];

  function EditToolbar() {
    return (
      <GridToolbarContainer sx={{paddingBottom: 1}}>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          endIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={readOnly}
        >
          Agregar Alcance de Trabajo
        </Button>
      </GridToolbarContainer>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 384, typography: 'body1' }} mt={1}>

      <ConfirmationModal
        open={confirmationModalOpen}
        onConfirm={onConfirm}
        onCancel={handleConfirmationModalClose} />

      <WorkScopeModal
        isOpen={modalOpen}
        onSubmit={onSubmit}
        onClose={handleModalClose}
        workScope={selectedWorkScope}
        techniques={techniques}
        users={users} />

      <CssVarsProvider>
        <DataGrid
          slots={{toolbar: EditToolbar}}
          rows={serviceOrder.workScopes}
          columns={columns}
          rowHeight={25}
          columnHeaderHeight={30}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10]}
          localeText={{ noRowsLabel: "No Hay Presupuestos" }}
          disableRowSelectionOnClick
        />

      </CssVarsProvider>
    </Box>
  );
};

export default WorkScopesList;
